import type { PathDocument } from './PathDocumentsCard'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { documentFilePath, GQL_ACKNOWLEDGE_DOCUMENT } from '~/gql/documents/documents'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { downloadFromApi } from '~/helpers/files/download-from-api'
import PathDocumentsCard from './PathDocumentsCard'

/**
 * Les documents du parcours, branchés à l'API (#326) : téléchargement, et accusé
 * de lecture. Une fois accepté le dernier document requis, `mySessions` relu
 * rouvre les modules.
 */
function PathDocuments({ documents }: { documents: readonly PathDocument[] }) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null)
  const [acknowledge] = useMutation(GQL_ACKNOWLEDGE_DOCUMENT, { refetchQueries: [GQL_MY_SESSIONS], awaitRefetchQueries: true })

  const onAcknowledge = async (document: PathDocument) => {
    setAcknowledgingId(document.id)
    try {
      await acknowledge({ variables: { id: document.id } })
    } catch {
      toast.error(t('pathDocuments.acknowledgeFailed'))
    } finally {
      setAcknowledgingId(null)
    }
  }

  return (
    <PathDocumentsCard
      documents={documents}
      formatDate={value => formatSessionDate(value, i18n.language, timezone, DATE_WITH_YEAR)}
      onDownload={d => void downloadFromApi(documentFilePath(d.id), `${d.title}.pdf`).catch(() => toast.error(t('pathDocuments.downloadFailed')))}
      onAcknowledge={d => void onAcknowledge(d)}
      acknowledgingId={acknowledgingId}
    />
  )
}

export default PathDocuments
